// Pure rules for how long a held or ringing SDK invite stays actionable in a
// tab. Twilio's cancel can fail to reach a leg (dead signaling), and that Call
// then reports 'pending' forever; these rules stop such a leg from ringing.

// Backend dials for 25 s and a redial adds a second window; the app ring
// already caps at 30 s.
export const INVITE_MAX_AGE_MS = 35000;

export const INVITE_SWEEP_MS = 5000;

const ENDED_RELAY_REASONS = new Set(['answered', 'declined', 'no_answer', 'canceled', 'ended']);

const isTime = (t) => typeof t === 'number' && Number.isFinite(t) && t > 0;

/** Unknown times are never stale, so a missing timestamp cannot mute a call. */
export const isInviteStale = (receivedAt, now) =>
  isTime(receivedAt) && now - receivedAt > INVITE_MAX_AGE_MS;

/** The earliest known moment this call started ringing anywhere for the agent. */
export const inviteOrigin = (incoming) => {
  const times = [incoming?.inviteReceivedAt, incoming?.relayAt].filter(isTime);
  return times.length > 0 ? Math.min(...times) : null;
};

export const inviteAgeMs = (incoming, now) => {
  const origin = inviteOrigin(incoming);
  return origin === null ? null : now - origin;
};

/** A ring may start again only while the call's first ring in this tab is within budget. */
export const canStartRing = (firstRungAt, now) => !isInviteStale(firstRungAt, now);

export const shouldRingOnPromotion = ({ callStatus, incoming, firstRungAt, now }) =>
  callStatus === 'pending' &&
  !isInviteStale(inviteOrigin(incoming), now) &&
  canStartRing(firstRungAt, now);

/** The sweep only drops a call that is still unanswered in this tab. */
export const shouldExpireInvite = ({ callStatus, incoming, now }) =>
  callStatus === 'pending' && isInviteStale(inviteOrigin(incoming), now);

export const endedRelayReason = (reason) => (ENDED_RELAY_REASONS.has(reason) ? reason : 'unknown');

const endedRelayMatches = (relay, incoming, callUUID) => {
  if (relay.call_uuid && callUUID && relay.call_uuid === callUUID) return true;
  const sids = incoming.ringSids || [];
  return [relay.call_sid, relay.child_call_sid].some((sid) => !!sid && sids.includes(sid));
};

/**
 * True when a server `incoming_call_ended` notice must drop this tab's invite.
 * The notice reaches every tab, including the one that answered, and can
 * repeat. Only an invite this tab is not answering is dropped: once accept()
 * runs the SDK leaves 'pending', and 'connecting' covers the gap before it.
 * The child leg sid alone cannot identify the answering tab, since every
 * registration of one identity rings on the same Client leg.
 */
export const shouldDropOnEndedRelay = ({ relay, incoming, callUUID, callStatus, uiStatus }) => {
  if (!relay || !incoming) return false;
  if (callStatus !== 'pending') return false;
  if (uiStatus === 'connecting') return false;
  return endedRelayMatches(relay, incoming, callUUID);
};
